import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, KeyRound } from 'lucide-react';
import { useLiqAuth } from '../auth';
import { api } from '../api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function Login() {
  const { adopt } = useLiqAuth();
  const nav = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [busy, setBusy] = useState(false);
  const googleRef = useRef(null);
  const gInit = useRef(false);

  // ---- Google Identity Services ----
  const handleGoogleCred = async (response) => {
    setErr(''); setBusy(true);
    try {
      const r = await api.post('/auth/google/token', { credential: response.credential });
      adopt(r.data.token, r.data.employee);
      nav('/liq');
    } catch (x) {
      setErr(x?.response?.data?.detail || 'Google login failed');
    } finally { setBusy(false); }
  };
  const [gError, setGError] = useState('');
  useEffect(() => {
    let attempts = 0;
    const tryInit = () => {
      if (gInit.current) return;
      attempts += 1;
      if (attempts > 40) { // ~5s
        setGError('Google Sign-in unavailable on this domain. Contact support to add this URL to Google OAuth origins, or use email OTP below.');
        return;
      }
      if (!window.google?.accounts) { setTimeout(tryInit, 120); return; }
      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCred,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        if (googleRef.current) {
          window.google.accounts.id.renderButton(googleRef.current, {
            type: 'standard', theme: 'filled_black', size: 'large',
            text: 'continue_with', shape: 'pill', width: 360,
          });
        }
        gInit.current = true;
      } catch (e) { setGError('Google Sign-in unavailable — use email OTP below.'); }
    };
    tryInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- OTP flow ----
  const sendCode = async (e) => {
    e.preventDefault();
    setErr(''); setOk(''); setBusy(true);
    try {
      const r = await api.post('/auth/send-otp', { email });
      setStep('otp');
      setOk(r.data.emailSent ? 'Code sent — check your inbox.' : 'Code created (email delivery unavailable — check server log).');
    } catch (x) {
      setErr(x?.response?.data?.detail || 'Failed to send code');
    } finally { setBusy(false); }
  };
  const verifyCode = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const r = await api.post('/auth/verify-otp', { email, otp: otp.trim() });
      adopt(r.data.token, r.data.employee);
      nav('/liq');
    } catch (x) {
      setErr(x?.response?.data?.detail || 'Verification failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen text-slate-100 flex items-center justify-center px-4 relative overflow-hidden"
         style={{ background: 'radial-gradient(1200px 700px at 15% -20%, rgba(201,169,97,0.08), transparent), radial-gradient(1000px 700px at 100% 100%, rgba(16,185,129,0.06), transparent), #06080f' }}
         data-testid="liq-login">
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
           style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22 opacity=%220.5%22/></svg>')" }} />

      {/* Load Google Identity Services */}
      <GoogleScript />

      <div className="relative w-full max-w-[440px]">
        <div className="text-center mb-6">
          <div className="inline-flex flex-col items-center leading-none">
            <span className="text-[28px] tracking-[0.18em] font-serif" style={{ color: '#e6c76d', textShadow: '0 0 24px rgba(230,199,109,0.15)' }}>
              LEGAL <span className="font-normal">IQ</span>
            </span>
            <span className="mt-2 text-[9.5px] tracking-[0.32em] uppercase text-amber-200/40">Intelligence · Advantage</span>
          </div>
        </div>

        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-8"
             style={{ boxShadow: '0 24px 60px -20px rgba(0,0,0,0.7)' }}>
          <div className="text-center mb-6">
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-200/60 mb-2">Operations Platform</div>
            <h1 className="font-serif text-[26px] leading-tight text-slate-50">Welcome back<span className="text-amber-300">.</span></h1>
            <p className="text-slate-500 text-[12px] mt-2">Sign in with your StartEase account</p>
          </div>

          {step === 'email' && (
            <>
              {/* Google button */}
              <div className="flex justify-center mb-3" ref={googleRef} data-testid="google-login-btn" />
              {gError && (
                <div className="text-amber-200/80 text-[11px] bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3" data-testid="google-error">
                  {gError}
                </div>
              )}

              {/* Divider */}
              <div className="flex items-center gap-4 mb-5">
                <div className="flex-1 h-px bg-white/[0.06]" />
                <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500">or</span>
                <div className="flex-1 h-px bg-white/[0.06]" />
              </div>

              <form onSubmit={sendCode} className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Email address</div>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                         placeholder="santosh@starteaseai.com"
                         className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-4 py-2.5 text-[13.5px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40 focus:bg-white/[0.05]"
                         data-testid="email-input" />
                </div>
                {err && <div className="text-rose-200 text-[12px] bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="liq-login-err">{err}</div>}
                <button type="submit" disabled={busy}
                        className="w-full mt-2 group inline-flex items-center justify-center gap-2 bg-gradient-to-b from-emerald-400 to-emerald-500 text-emerald-950 font-semibold text-[14px] py-3 rounded-full shadow-lg disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 hover:scale-[1.01] transition-all"
                        data-testid="send-otp-btn">
                  {busy ? 'Sending…' : 'Send Login Code'}
                  {!busy && <ArrowRight size={14} strokeWidth={2.5} className="group-hover:translate-x-0.5 transition-transform" />}
                </button>
              </form>
            </>
          )}

          {step === 'otp' && (
            <form onSubmit={verifyCode} className="space-y-4" data-testid="liq-otp-form">
              <div className="flex items-center justify-center gap-2 text-emerald-200/80 text-[12px]">
                <KeyRound size={13} /> Code sent to <strong className="text-emerald-100">{email}</strong>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">6-digit code</div>
                <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                       required inputMode="numeric" autoFocus placeholder="000000"
                       className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-4 py-3 text-center text-[22px] tracking-[0.5em] font-mono text-slate-100 placeholder-slate-700 outline-none focus:border-amber-500/40 focus:bg-white/[0.05]"
                       data-testid="otp-input" />
              </div>
              {ok && <div className="text-emerald-200 text-[11px] bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">{ok}</div>}
              {err && <div className="text-rose-200 text-[12px] bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">{err}</div>}
              <button type="submit" disabled={busy || otp.length !== 6}
                      className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-b from-emerald-400 to-emerald-500 text-emerald-950 font-semibold text-[14px] py-3 rounded-full shadow-lg disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 hover:scale-[1.01] transition-all"
                      data-testid="verify-otp-btn">
                {busy ? 'Verifying…' : 'Verify & Sign in'}
                {!busy && <ShieldCheck size={14} strokeWidth={2.5} />}
              </button>
              <button type="button" onClick={() => { setStep('email'); setOtp(''); setErr(''); setOk(''); }}
                      className="w-full text-slate-500 hover:text-amber-200 text-[11px] tracking-wider">
                ← Use a different email
              </button>
            </form>
          )}

          <div className="mt-6 pt-4 border-t border-white/[0.05] text-center text-slate-600 text-[10.5px] tracking-wider">
            Any StartEase team member can sign in and collaborate
          </div>
        </div>

        <div className="text-center text-slate-600 text-[10px] tracking-[0.2em] uppercase mt-6">
          A StartEase workspace · <a href="https://starteaseai.com" className="text-amber-200/60 hover:text-amber-200 no-underline">starteaseai.com</a>
        </div>
      </div>
    </div>
  );
}

function GoogleScript() {
  useEffect(() => {
    if (document.getElementById('gis-script')) return;
    const s = document.createElement('script');
    s.id = 'gis-script';
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    document.head.appendChild(s);
  }, []);
  return null;
}
